import { useMutation } from '@apollo/client/react/hooks';
import InfoHollow from '@clearkit/icons/glyphs/InfoHollow';
import {
  CKBadge,
  CKButton,
  CKCardCollapsible,
  CKModal,
  CKTooltip,
} from 'clearkit';
import capitalize from 'lodash/capitalize';

import ToastsContainer from '~/containers/Toasts';
import { readableType } from '~/lib/dataEnrichment';

import cancel from './cancel.graphql';
import trigger from './trigger.graphql';

const BackfillTriggerFields = ({
  integration,
  objectType,
  accountConnector,
  isActive,
  setTriggering,
  hasEnrichmentBetaUIFlag,
  hasSavedSelectiveEnrichmentConfig,
  accountConnectorBackfillActive,
  setAccountConnectorBackfillActive,
}) => {
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);

  const [triggerBackfill, { loading: triggering }] = useMutation(trigger, {
    onCompleted: () => {
      setTriggering(false);
      setAccountConnectorBackfillActive(true);
      ToastsContainer.addSuccess('Backfill enrichment started');
    },
    onError: () => {
      setTriggering(false);
      ToastsContainer.addWarning(
        `An error occurred with initiating your backfill enrichment, please try again.`,
      );
    },
    refetchQueries: ['FieldMappingForm'],
    variables: {
      input: {
        id: accountConnector?.id,
        targetObjectType: objectType.name,
      },
    },
  });

  const [cancelBackfill, { loading: cancelling }] = useMutation(cancel, {
    onCompleted: () => {
      setAccountConnectorBackfillActive(false);
      ToastsContainer.addSuccess(
        `${capitalize(integration)} ${capitalize(
          objectType.name,
        )} backfill cancelled!`,
      );
    },
    onError: () => {
      ToastsContainer.addWarning(
        `An error occurred with cancelling your backfill enrichment, please try again.`,
      );
    },
    refetchQueries: ['FieldMappingForm'],
    variables: {
      input: {
        id: accountConnector?.id,
        targetObjectType: objectType.name,
      },
    },
  });

  const startBackfill = () => {
    setTriggering(true);
    triggerBackfill().then(() => setConfirmationModalOpen(false));
  };

  const backfillLoading = triggering || cancelling;

  return hasEnrichmentBetaUIFlag ? (
    <SelectiveEnrichmentBackfillConfig
      integration={integration}
      objectType={objectType}
      accountConnectorBackfillActive={accountConnectorBackfillActive}
      backfillLoading={backfillLoading}
      cancelBackfill={cancelBackfill}
      startBackfill={() => startBackfill()}
      isActive={isActive}
      hasSavedSelectiveEnrichmentConfig={hasSavedSelectiveEnrichmentConfig}
      hasEnrichmentBetaUIFlag={hasEnrichmentBetaUIFlag}
      confirmationModalOpen={confirmationModalOpen}
      onOpen={() => setConfirmationModalOpen(true)}
      onDismiss={() => setConfirmationModalOpen(false)}
    />
  ) : (
    <BaseBackfillConfig
      integration={integration}
      objectType={objectType}
      accountConnectorBackfillActive={accountConnectorBackfillActive}
      backfillLoading={backfillLoading}
      cancelBackfill={cancelBackfill}
      startBackfill={() => startBackfill()}
      isActive={isActive}
    />
  );
};

function SelectiveEnrichmentBackfillConfig({
  integration,
  objectType,
  accountConnectorBackfillActive,
  startBackfill,
  backfillLoading,
  cancelBackfill,
  isActive,
  hasSavedSelectiveEnrichmentConfig,
  hasEnrichmentBetaUIFlag,
  confirmationModalOpen,
  onOpen,
  onDismiss,
}) {
  return (
    <div className="px-4 mt-10 mb-6">
      <CKCardCollapsible>
        <SelectiveEnrichmentBackfillHeader
          integration={integration}
          objectType={objectType}
          accountConnectorBackfillActive={accountConnectorBackfillActive}
        />
        <CKCardCollapsible.Body>
          <div className="flex flex-row items-center justify-between">
            <div className="flex flex-col mr-20 w-96">
              <p className="text-base font-medium text-gray-800">
                Start a backfill enrichment on {readableType(integration)}{' '}
                {objectType.name}s
              </p>
            </div>
            {accountConnectorBackfillActive ? (
              <CKButton
                isLoading={backfillLoading}
                variant="simple"
                variantColor="red"
                onClick={onOpen}>
                Cancel Backfill
              </CKButton>
            ) : (
              <StartBackfillButton
                disabled={!isActive}
                hasEnrichmentBetaUIFlag={hasEnrichmentBetaUIFlag}
                onClick={onOpen}
              />
            )}
          </div>
          {hasSavedSelectiveEnrichmentConfig ? (
            <SelectiveEnrichmentBackfillFooter
              integration={integration}
              objectType={objectType}
            />
          ) : null}
        </CKCardCollapsible.Body>
      </CKCardCollapsible>
      <ConfirmationModal
        isActive={accountConnectorBackfillActive}
        isLoading={backfillLoading}
        confirmationModalOpen={confirmationModalOpen}
        onClick={onDismiss}
        integration={integration}
        objectType={objectType}
        startBackfill={startBackfill}
        cancelBackfill={cancelBackfill}
      />
    </div>
  );
}

function SelectiveEnrichmentBackfillFooter({ integration, objectType }) {
  return (
    <CKCardCollapsible.Footer className="flex flex-row items-center bg-gray-0">
      <div className="flex flex-col mr-4">
        <InfoHollow />
      </div>
      Selective sync is on. Enriched {objectType.name} data will only be synced
      to {readableType(integration)} for {objectType.name}s that meet your
      selective sync criteria.
    </CKCardCollapsible.Footer>
  );
}

function SelectiveEnrichmentBackfillHeader({
  integration,
  objectType,
  accountConnectorBackfillActive,
}) {
  return (
    <CKCardCollapsible.Header>
      <div
        className="grid grid-cols-[1fr,auto]"
        data-testid="backfillConfigCardHeader">
        <div className="text-base">
          <h3 className="mb-2 font-medium">Backfill enrichment</h3>
          <p>
            Use this to enrich existing {objectType.name}s found within{' '}
            {readableType(integration)}. This can be helpful if you’ve added
            field mappings and want these enriched fields synced to{' '}
            {objectType.name}s already in your CRM. This process may take some
            time.
          </p>
        </div>
        {accountConnectorBackfillActive ? (
          <CKBadge
            className="self-start text-xs text-green-700 bg-green-100"
            variant="default">
            Backfill running
          </CKBadge>
        ) : null}
      </div>
    </CKCardCollapsible.Header>
  );
}

function BaseBackfillConfig({
  integration,
  objectType,
  accountConnectorBackfillActive,
  startBackfill,
  backfillLoading,
  cancelBackfill,
  isActive,
}) {
  return (
    <div className="flex flex-row items-center justify-between pt-4">
      <div className="flex flex-col mr-20 w-96">
        <p className="mb-2 text-base font-medium text-gray-800">
          Backfill {readableType(integration)} {objectType.name} fields
        </p>
        <p className="max-w-3xl text-sm text-gray-600">
          Enrich all {objectType.name}s in {readableType(integration)}. This
          process may take some time.
        </p>
      </div>
      {accountConnectorBackfillActive ? (
        <CKButton
          isLoading={backfillLoading}
          variant="simple"
          variantColor="red"
          onClick={cancelBackfill}>
          Cancel Backfill
        </CKButton>
      ) : (
        <StartBackfillButton
          disabled={!isActive}
          onClick={startBackfill}
          isLoading={backfillLoading}
        />
      )}
    </div>
  );
}

function StartBackfillButton({
  disabled,
  isLoading,
  hasEnrichmentBetaUIFlag,
  onClick,
}) {
  if (disabled) {
    return (
      <CKTooltip tooltip="Enrichment needs to be enabled" placement="top-end">
        <CKButton
          isLoading={isLoading}
          variant={hasEnrichmentBetaUIFlag ? 'bold' : 'simple'}
          variantColor={hasEnrichmentBetaUIFlag ? 'blue' : 'default'}>
          Run Backfill
        </CKButton>
      </CKTooltip>
    );
  }

  return (
    <CKButton
      isLoading={isLoading}
      onClick={onClick}
      variant={hasEnrichmentBetaUIFlag ? 'bold' : 'simple'}
      variantColor={hasEnrichmentBetaUIFlag ? 'blue' : 'default'}>
      Run Backfill
    </CKButton>
  );
}

function ConfirmationModal({
  isActive,
  isLoading,
  confirmationModalOpen,
  onClick,
  integration,
  objectType,
  startBackfill,
  cancelBackfill,
}) {
  return (
    <CKModal
      aria-label="Modal"
      isVisible={confirmationModalOpen}
      onToggle={onClick}
      size="xs">
      <CKModal.Header className="pb-2">
        <CKModal.Heading>
          {isActive
            ? 'Are you sure you want to cancel this backfill enrichment?'
            : 'Are you sure you want to start a backfill enrichment?'}
        </CKModal.Heading>
      </CKModal.Header>
      <CKModal.Body className="px-10 pt-2 pb-10">
        <p>
          {isActive
            ? `Any ${capitalize(integration)} ${
                objectType.name
              } with data synced during this backfill enrichment will retain that data.`
            : `Enriched data for the field mappings you’ve set up will be synced for to all ${
                objectType.name
              }s in your ${capitalize(integration)} CRM.`}
        </p>
      </CKModal.Body>
      <CKModal.Footer className="gap-2">
        <CKButton isLoading={isLoading} onClick={onClick}>
          Dismiss
        </CKButton>
        {isActive ? (
          <CKButton
            variant="bold"
            variantColor="red"
            isLoading={isLoading}
            onClick={() => cancelBackfill().then(onClick)}>
            Yes, cancel this backfill enrichment
          </CKButton>
        ) : (
          <CKButton
            variant="bold"
            variantColor="blue"
            isLoading={isLoading}
            onClick={startBackfill}>
            Yes, enrich and sync data
          </CKButton>
        )}
      </CKModal.Footer>
    </CKModal>
  );
}

export default BackfillTriggerFields;
